import React from 'react';

import logo from 'shared_components/public/assets/Logo.png';
import { Link } from 'react-router-dom';
import { useSelectedDashboardPath } from 'shared_components/context/navigation';
import { WLink } from 'shared_components/components/WLink/WLink';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import clsx from 'clsx';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

function Sidebar({ isCollapsed, onToggle }: SidebarProps) {
  const selectedDashboardPath = useSelectedDashboardPath();
  const currentPath = location.pathname;
  const isDashboardPage = new RegExp(`^${selectedDashboardPath}$`).test(
    currentPath
  );

  const classes = clsx(
    {
      'tw-text-textLime': isDashboardPage,
    },
    {
      'tw-text-textWhite': !isDashboardPage,
    },
    'tw-cursor-pointer',
    'tw-hover:text-textLime',
    isCollapsed && 'tw-sr-only'
  );

  const discographyAdditionalPaths = ['registrations', 'discography'];
  const settingsAdditionalPaths = ['society-config'];

  return (
    <div className="tw-h-full tw-flex tw-flex-col tw-text-white tw-border-r-outlineGraphite">
      <div className={clsx('tw-p-4', isCollapsed && 'tw-px-3 tw-py-4')}>
        <div className="tw-pb-6">
          <div
            className={clsx(
              'tw-flex tw-items-center tw-justify-between',
              isCollapsed && 'tw-justify-center'
            )}
          >
            {!isCollapsed && (
              <img src={logo} alt="Westbury Music Logo" width="151" />
            )}
            {isCollapsed && (
              <img
                src={logo}
                alt="Westbury Music"
                className="tw-h-8 tw-w-8 tw-object-contain"
              />
            )}
          </div>
        </div>

        <div className="tw-flex tw-flex-col tw-space-y-6">
          <Link to={`${selectedDashboardPath}`}>
            <div
              className={clsx(
                'tw-flex tw-items-center tw-space-x-2',
                isCollapsed && 'tw-justify-center tw-space-x-0'
              )}
            >
              <i
                className={clsx(
                  'wi',
                  'wi-sm',
                  'wi-home',
                  {
                    'tw-text-textLime': isDashboardPage,
                  },
                  {
                    'tw-text-textWhite': !isDashboardPage,
                  },
                  'tw-cursor-pointer'
                )}
              />
              <span className={classes}>Dashboard</span>
            </div>
          </Link>
          <WLink
            label="Clients"
            to="/clients"
            icon="users"
            compact={isCollapsed}
          />
          <WLink
            label="Discography"
            to="/discography"
            icon="speakers"
            additionalPaths={discographyAdditionalPaths}
            compact={isCollapsed}
          />
          <WLink
            label="Distribution"
            to="/distribution"
            icon="gbp"
            variant="disabled"
            compact={isCollapsed}
          />
          <div className="tw-border-t tw-border-textGraphite tw-pt-8 tw-space-x-8">
            <WLink
              label="Settings"
              to="/settings"
              icon="settings"
              additionalPaths={settingsAdditionalPaths}
              compact={isCollapsed}
            />
            <WLink
              label="Messages"
              to="/messages"
              icon="email"
              variant="disabled"
              compact={isCollapsed}
            />
          </div>
        </div>
      </div>

      {/* Expand / collapse toggle */}
      <div className="tw-mt-auto tw-border-t tw-border-textGraphite tw-p-3">
        <button
          type="button"
          onClick={onToggle}
          className={clsx(
            'tw-flex tw-items-center tw-justify-center tw-w-full tw-py-2 tw-rounded tw-text-white tw-bg-transparent hover:tw-bg-white/10 tw-transition-colors',
            isCollapsed && 'tw-justify-center'
          )}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? (
            <ChevronRight className="tw-h-5 tw-w-5" />
          ) : (
            <>
              <ChevronLeft className="tw-h-5 tw-w-5 tw-mr-1" />
              <span className="tw-text-sm">Collapse</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default Sidebar;
